import { type Ref, ref } from 'vue'
import { requestFailure } from '@/api'
import { reportHandledError, reportNoAccess } from '@/sentry'

/** A screen's blocks, each loaded on its own: one that fails is named, reported, and cleared when it next loads. */
export interface BlockLoads {
    /** The blocks whose last load failed, by the name the screen shows for them, each listed once. */
    failed: Readonly<Ref<readonly string[]>>
    /**
     * Never rejects: a failed `fetch` is recorded against `block` and reported, and `apply` is not called. A `fetch`
     * the API refuses with 403 is not a failure: `apply` is not called, the block is not listed, and the refusal is
     * reported as a warning.
     */
    load: <T>(block: string, fetch: () => Promise<T>, apply: (value: T) => void) => Promise<void>
}

/**
 * Per-block load state for a screen built from independent reads (#1677). Each block keeps its own failure, so the
 * screen can say which block failed rather than "some of" it; a block's next good load clears its own failure and
 * no other; and every failure goes to Sentry through `reportHandledError`, so a handled failure is still seen.
 *
 * `failed` lists blocks in the order they first failed. A block that fails again stays where it is, and is reported
 * again: each failed read is its own event.
 *
 * A read the API refuses with 403 is a third outcome, beside loaded and failed (#1801, OS-PORTAL-5). It answers "may
 * I read this?", not "could I read this?": the caller's grant does not cover the block, which is the permission model
 * working. So the block gets nothing to show and is not listed as failed, and the refusal goes to Sentry through
 * `reportNoAccess`, as a warning under its own fingerprint, never as an error. Since a refusal is not a failure, it
 * also clears a failure the block had before, the way a good load does. Only 403 is treated so: a 401 (no session),
 * a 404, a 5xx, a network error and a fault in the screen's own code all fail as before.
 */
export function useBlockLoads(): BlockLoads {
    const failed = ref<string[]>([])

    async function load<T>(block: string, fetch: () => Promise<T>, apply: (value: T) => void): Promise<void> {
        try {
            apply(await fetch())
        } catch (caught) {
            const refusal = requestFailure(caught)
            if (refusal?.status !== 403) {
                if (!failed.value.includes(block)) {
                    failed.value = [...failed.value, block]
                }
                reportHandledError(caught, block)
                return
            }
            reportNoAccess(refusal, block)
        }
        if (failed.value.includes(block)) {
            failed.value = failed.value.filter(name => name !== block)
        }
    }

    return { failed, load }
}
